import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { useTranslation } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = ({ lang = 'en' }) => {
  const { t } = useTranslation(lang);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('profile.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('profile.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/password-reset/reset`, {
        token,
        new_password: password
      });
      
      toast.success(t('resetPassword.passwordChanged'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || t('profile.passwordChangeFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-secondary)' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>{t('resetPassword.invalidLink')}</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{t('resetPassword.linkExpired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-md w-full space-y-8">
        <div className="rounded-lg shadow-xl p-8" style={{ background: 'var(--bg-primary)' }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: 'var(--accent)' }}>
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
              {t('resetPassword.title')}
            </h2>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
              {t('resetPassword.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                type="password"
                placeholder={t('resetPassword.newPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <div>
              <Input
                type="password"
                placeholder={t('resetPassword.confirmPassword')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('resetPassword.changing') : t('resetPassword.changePassword')}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
